// Page layout's drawn preview (AdminLayout.dc.html, phase 1): the shown sections in order, in the
// product's look, with the board's sample lines, at laptop width or in a phone's frame. It's a
// drawing, as signed off: the real page can't show a draft or a draft product (the map's question,
// settled for the drawn preview).
import { LAYOUT } from './copy.js'
import { layoutKind } from '../../product/layout.js'

// The board's surfaces per section (phase 1 doesn't change them; phase 2's builder will). The
// Accelerator's and Profit On Your Terms's homes draw every section as a card (their boards).
const SURFACE = { news: 'page', next: 'card', resume: 'page', weeks: 'page', todo: 'card', help: 'card', res: 'page', wnews: 'page', video: 'page', summary: 'card', five: 'card', learn: 'page', hw: 'card', sheet: 'page', prompts: 'page', where: 'card' }
const CARDS = new Set(['goal', 'next', 'actions', 'group', 'wins', 'recordings', 'start', 'tool', 'help', 'weeks', 'guarantee', 'surgery'])
const COLS = { res: 3, video: 2, prompts: 2 }

// The Accelerator's home (Accelerator.dc.html, Returning member) and Profit On Your Terms's
// (POYT.dc.html, Paid in full), in their boards' sample words
// (the home made shorter by the Accelerator critique's member 5, 20 Oct: the latest recording, and a shortcut to each
// Resources tab with its count)
const MASTERMIND_SAMPLES = {
  goal: { lines: [['Replace my salary with property income by September 2027.', true], ['Month 3 of 12']] },
  next: { big: 'SUN 25 OCT', lines: [['5pm to 8:30pm on Zoom, Group 2'], ['2 of 2 sessions attended live. On track']], button: 'Add to calendar' },
  actions: { lines: [['[x] Run 3 deals through the calculator', true], ['[ ] Get the roof quote on the 3-bed in writing'], ['1 of 5 ticked']] },
  group: { lines: [['Group 2', true], ["The group's WhatsApp, where you talk between sessions"]], button: 'Join the group' },
  wins: { lines: [['First direct to vendor letters posted', true], ['Marketing, 14 Sep']], button: 'Add a win' },
  recordings: { tiles: ['Group 2, session 2'], stack: true },
  res: { tiles: ['Calculators 7', 'Templates 3', 'Guides 12', 'Training 2'] },
}
const POYT_SAMPLES = {
  start: { lines: [['1 of 2 done', true], ['Deal analysis'], ['Marketing setup']], button: 'Start' },
  tool: { lines: [['The two-offer analyser', true], ['Type in an asking price and both offers build on screen.']], button: 'Open the analyser' },
  help: { lines: [['Email course@adamslee.co.uk. Adam answers them himself.']] },
  weeks: { tiles: ['WEEK 1  Wed 21 Oct, 7pm', 'WEEK 2  Wed 28 Oct, 7pm', 'WEEK 3  Wed 4 Nov, 7pm'], tall: '28px', stack: true },
  // the Resources tabs: the board's Start here tab is Before Week 1, as live renamed it on 12 Oct (Adam's yes)
  res: { tiles: ['Before Week 1', 'Marketing toolkit', 'Weekly packs', 'Tools', 'Bonuses'] },
  guarantee: { lines: [["If a vendor says something the scripts don't cover, send it to Adam and he writes yours."]] },
  surgery: { lines: [['30 minutes with Adam on your first live deal.']], button: 'Book your 30 minutes' },
}

// Each section's sample lines, as the board draws them (The Funding Engine's), and the on-demand
// home's own (no board draws them)
function sample(key, onDemand, kind) {
  if (kind === 'mastermind' && MASTERMIND_SAMPLES[key]) return MASTERMIND_SAMPLES[key]
  if (kind === 'poyt' && POYT_SAMPLES[key]) return POYT_SAMPLES[key]
  switch (key) {
    case 'news': case 'wnews': return { lines: [["From Adam: [the announcement, while there's 1]", true]] }
    case 'next':
      if (onDemand) return { lines: [['Up next', true], ['Module 2, part 3: [the recording to watch next]']], button: 'Carry on' }
      return { big: 'WEEK 4 IS LIVE TONIGHT', lines: [['7pm on Zoom. The button opens at 6:45pm.']], button: 'Join opens at 6:45pm' }
    case 'resume': return { lines: [['Week 3: Getting seen', true], ['You stopped at 18:35']], button: 'Carry on from 18:35' }
    case 'weeks': return { tiles: ['WEEK 1  Watched', 'WEEK 2  Watched', 'WEEK 3  Carry on from 18:35', 'WEEK 4  Live tonight'], tall: '28px', stack: true }
    case 'todo': return { lines: [['[ ] Make 2 of your 5 posts this week video'], ['[ ] Keep the bar: 5 posts minimum this week']] }
    case 'help': return { lines: [['WhatsApp Adam. Anything you want his eyes on before it goes out, send it over.'], ['Message Adam on WhatsApp', true]] }
    case 'res': return { tiles: ['Pillar canvas', '[Week 1 worksheet]', '[Week 3 worksheet]'] }
    case 'video': return { tiles: ['The recording', 'Chapters: 08:51 Part 1, 45:33 Part 2'], tall: '96px' }
    case 'summary': return { lines: [['Week 1 gave you the foundations: your positioning, your profile, your avatar and the rules. Week 2 is where the engine starts.']] }
    case 'five': return { lines: [['1. Your unfair advantage picks your pillars.', true], ['2. Video is the trust shortcut.', true]] }
    case 'learn': return { lines: [['08:51  Your unfair advantage'], ['45:33  Video first, and what each format is for']] }
    case 'hw': return onDemand ? { lines: [["[ ] [The homework for the module they're on]"]] } : sample('todo', false, null)
    case 'sheet': return { tiles: ['Pillar canvas, PDF'] }
    case 'prompts': return { tiles: ['Prompt 1: Pillars', 'Prompt 2: Hooks', 'Prompt 3: Captions'] }
    case 'where': return { big: 'MODULE 2 OF 5', lines: [['Money: 3 of 5 recordings watched', true], ['Homework: 2 of 4 ticked']] }
    default: return { lines: [] }
  }
}

export function LayoutPreview({ product, page, sections, words, phone }) {
  const fe = product.look === 'funding_engine'
  // the product frame draws the family looks and, for anything else (an own look included), the master brand
  const look = fe ? 'fe' : product.look === 'accelerator' ? 'acc' : product.look === 'poyt' ? 'poyt' : 'master'
  const onDemand = product.type === 'on_demand'
  const kind = layoutKind(product)
  // the Accelerator's menu (Introductions is left out, Adam, 5 Oct; Actions after Home, the critique's member 14)
  const nav = onDemand ? ['Home', 'Recordings', 'Resources']
    : kind === 'mastermind' ? ['Home', 'Actions', 'Sessions', 'Recordings', 'Resources', 'Group']
      : ['Home', 'Sessions', 'Recordings', 'Resources']
  const lit = page === 'home' ? 'Home' : (onDemand ? 'Recordings' : 'Sessions')
  const shown = sections.filter(s => s.shown)
  return (
    <div className="ap-pv-col">
      <div className="ap-pv-head">
        <span className="ap-pv-title">{phone ? LAYOUT.previewPhone : LAYOUT.previewLaptop}</span>
        <span className="ap-pv-note">{phone ? LAYOUT.notePhone : LAYOUT.noteLaptop}</span>
      </div>
      <div className={`ap-pv ap-pv--${look}${phone ? ' ap-pv--phone' : ''}`} aria-label={phone ? LAYOUT.previewPhone : LAYOUT.previewLaptop} role="img">
        <div className="ap-pv-bar">
          <span className="ap-pv-brand"><span className="ap-pv-lib">Library</span><span className="ap-pv-name">{fe ? product.name.toUpperCase() : product.name}</span></span>
          <span className="ap-pv-ring" />
        </div>
        <div className="ap-pv-nav">{nav.map(n => <span key={n} className={`ap-pv-tab${n === lit ? ' ap-pv-tab--on' : ''}`}>{n}</span>)}</div>
        <div className="ap-pv-body">
          {shown.map(s => {
            const x = sample(s.key, onDemand, page === 'home' ? kind : null)
            const ownCards = page === 'home' && (kind === 'mastermind' || kind === 'poyt') && CARDS.has(s.key)
            const surface = s.surface && s.surface !== 'page' ? s.surface : ownCards ? 'card' : (SURFACE[s.key] || 'page')
            const cols = phone || x.stack ? 1 : Math.max(1, Math.min(3, COLS[s.key] || 1))
            return (
              <div key={s.key} className={`ap-pv-sec ap-pv-sec--${surface}`}>
                <span className="ap-pv-h">{words(s.key).name.toUpperCase()}</span>
                {x.big && <span className={`ap-pv-big${phone ? ' ap-pv-big--phone' : ''}`}>{x.big}</span>}
                {(x.lines || []).map(([t, strong]) => <span key={t} className={`ap-pv-line${strong ? ' ap-pv-line--strong' : ''}`}>{t}</span>)}
                {x.tiles && (
                  <span className="ap-pv-tiles" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                    {x.tiles.map(t => <span key={t} className="ap-pv-tile" style={{ minHeight: x.tall || '44px' }}>{t}</span>)}
                  </span>
                )}
                {x.button && <span className="ap-pv-btn">{x.button}</span>}
              </div>
            )
          })}
          {shown.length === 0 && <span className="ap-pv-empty">{LAYOUT.allHidden}</span>}
        </div>
      </div>
    </div>
  )
}
